import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  AUTH_MESSAGES,
  SERVER_MESSAGES,
  VALIDATION,
  isServerMessage,
  mapAuthError,
  readAuthError,
} from 'layouts/auth';

/**
 * Gọi API cho trang Đăng ký (đặc tả §4, phương án A). Cùng endpoint và cùng body với màn đăng
 * ký của app HP Media (HP-Media/src/app/features/un-authen/sign-up, veryfy):
 *
 * 1. GET  users/check?username=<TikTok ID>  → 200 còn trống / 409 'User already exists'
 * 2. POST auth/register { email, nickName, phoneNumber, username, password }
 *    → 201 { email, ... } và gửi mã 4 số qua email. KHÔNG gửi department / defaultShift / code:
 *    trang quản trị không cho tự khai phòng ban (tránh cấp isHpCompany không ai duyệt).
 * 3. POST users/verify { email, code } → { status: 'User verify done' }
 *
 * "Gửi lại mã" gọi lại đúng bước 2 với cùng dữ liệu, như khi người dùng app bấm Đăng ký lần nữa:
 * backend thấy email chưa xác minh thì chỉ sinh mã mới (ghi đè mã cũ) và gửi lại email.
 * Backend gửi email TRƯỚC khi lưu, nên lỗi gửi mail (5xx) không tạo tài khoản dở dang.
 */

export interface RegisterPayload {
  email: string;
  phoneNumber: string;
  nickName: string;
  password: string;
  /** TikTok ID. */
  username: string;
}

export const REGISTER_MESSAGES = {
  emailTaken: 'Email này đã có tài khoản. Hãy đăng nhập hoặc dùng Quên mật khẩu.',
  tiktokTaken: VALIDATION.tiktokTaken,
  tiktokFormat: VALIDATION.tiktokFormat,
  /** Sau khi đã sang bước nhập mã: TikTok ID vừa bị người khác lấy mất. */
  tiktokTakenOnResend: 'TikTok ID này vừa có người dùng. Bấm "Sửa thông tin" để đổi TikTok ID.',
  rateLimited: 'Bạn thao tác quá nhanh. Vui lòng đợi khoảng 20 giây rồi thử lại.',
  // Backend không cho mã hết hạn theo giờ, nhưng mỗi lần gửi lại thì mã cũ bị thay.
  codeWrong:
    'Mã không đúng hoặc đã được thay bằng mã mới. Dùng mã trong email mới nhất hoặc bấm Gửi lại mã.',
  codeRequired: VALIDATION.codeRequired,
  codeFormat: VALIDATION.codeFormat,
  tiktokChecking: 'Đang kiểm tra TikTok ID…',
  tiktokAvailable: 'TikTok ID này dùng được.',
  tiktokCheckFailed:
    'Chưa kiểm tra được TikTok ID. Hệ thống sẽ kiểm tra lại khi bạn bấm Tạo tài khoản.',
  rulesFailed: 'Chưa tải được Quy tắc cộng đồng. Vui lòng thử lại.',
  rulesEmpty: 'Chưa có nội dung Quy tắc cộng đồng. Liên hệ Admin nếu bạn cần xem trước.',
} as const;

/** Đăng ký (hoặc gửi lại mã). Trả về email backend xác nhận, như app đọc `response.data.email`. */
export async function registerAccount(payload: RegisterPayload): Promise<string> {
  const res: any = await AxiosUtils.post(API.register, payload);
  const email = res?.data?.email;
  return typeof email === 'string' && email ? email : payload.email;
}

export async function verifyAccount(email: string, code: string): Promise<void> {
  await AxiosUtils.post(API.verifyUser, { email, code });
}

export type TiktokAvailability = 'available' | 'taken' | 'error';

/** Kiểm tra TikTok ID còn trống. Không bao giờ ném: lỗi mạng / 5xx / 429 trả 'error'. */
export async function checkTiktokId(username: string): Promise<TiktokAvailability> {
  try {
    await AxiosUtils.get(API.checkUsername, { username });
    return 'available';
  } catch (error) {
    return readAuthError(error).status === 409 ? 'taken' : 'error';
  }
}

/** Nội dung Quy tắc cộng đồng (HTML do Admin soạn ở trang Chính sách). GET /rules công khai. */
export async function fetchCommunityRules(): Promise<string> {
  const res: any = await AxiosUtils.get(API.rules);
  const content = res?.data?.content;
  return typeof content === 'string' ? content : '';
}

export type RegisterErrorResult =
  | { kind: 'emailTaken' }
  | { kind: 'field'; field: 'username'; message: string }
  | { kind: 'alert'; message: string };

/**
 * Lỗi của POST auth/register. 409 có hai nghĩa, phân biệt bằng `message`:
 * 'Tiktok ID already exists' → lỗi ở ô TikTok ID; 'User already exists' → email đã có tài khoản.
 */
export function mapRegisterError(error: unknown): RegisterErrorResult {
  if (isServerMessage(error, SERVER_MESSAGES.TIKTOK_EXISTS)) {
    return { kind: 'field', field: 'username', message: REGISTER_MESSAGES.tiktokTaken };
  }
  const { status, serverMessage } = readAuthError(error);
  if (status === 409) return { kind: 'emailTaken' };
  if (status === 400 && serverMessage.toLowerCase().includes('username format wrong')) {
    return { kind: 'field', field: 'username', message: REGISTER_MESSAGES.tiktokFormat };
  }
  return {
    kind: 'alert',
    message: mapAuthError(error, {
      rateLimited: REGISTER_MESSAGES.rateLimited,
      // 5xx ở bước này gần như luôn do máy chủ gửi email lỗi (bản dev không có khoá gửi mail).
      server: AUTH_MESSAGES.emailSendFailed,
    }),
  };
}

export const isCodeWrong = (error: unknown) => isServerMessage(error, SERVER_MESSAGES.CODE_WRONG);

/** Lỗi của POST users/verify sau khi đã loại trừ "sai mã". */
export const mapVerifyError = (error: unknown) =>
  mapAuthError(error, { rateLimited: REGISTER_MESSAGES.rateLimited });
